// src/api/clients.ts
import api from "./api";

export interface ClientBranch {
  id?: string;
  branchName: string;
  gstin?: string | null;
  address1?: string | null;
  address2?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  // Geofence for clocking in at this site — all three or none.
  latitude?: string | null;
  longitude?: string | null;
  radius_meters?: number | null;
  linked_branch_id?: string | null;
}

export interface Client {
  id: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  gstin: string | null;
  pan: string | null;
  // Geofence on this entity's own address, so an employee visiting them can
  // clock in there. All three or none — a partial set is not a fence.
  latitude?: string | null;
  longitude?: string | null;
  radius_meters?: number | null;
  // Who referred this client. `reference_name` is denormalized by the backend
  // so the card and the edit form render without a second lookup.
  reference_id?: string | null;
  reference_name?: string | null;
  user_id: string;
  branchID: string | null;
  branches?: ClientBranch[];
  // Cross-org link metadata. When `is_linked` is true the row mirrors the
  // basic_details profile of another Teamlio org and is read-only here.
  linked_headOfficeID?: string | null;
  linked_user_id?: string | null;
  is_linked?: boolean;
  // Messenger conversation id of the shared workspace group between the two
  // orgs. Populated for linked rows once the messenger webhook succeeds; the
  // "Open workspace" button is hidden while null (e.g. webhook queued).
  // The backend has always returned this (routers/clients.py serializes it) —
  // the field was just missing from this type, unlike Vendor's.
  cross_org_group_id?: string | null;
}

export interface ClientCreatePayload {
  clientName: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  // Sent as a SET: the backend validates them together. radius_meters 0
  // removes the fence.
  latitude?: string | null;
  longitude?: string | null;
  radius_meters?: number | null;
  // The name, not an id — the backend get-or-creates it. On update, omitting
  // the key leaves the reference unchanged; "" clears it.
  reference_name?: string;
  branchDetails?: ClientBranch[];
}

export interface ClientUpdatePayload {
  clientName?: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  // Sent as a SET: the backend validates them together. radius_meters 0
  // removes the fence.
  latitude?: string | null;
  longitude?: string | null;
  radius_meters?: number | null;
  // The name, not an id — the backend get-or-creates it. On update, omitting
  // the key leaves the reference unchanged; "" clears it.
  reference_name?: string;
  branchDetails?: ClientBranch[];
}

export interface GstinLookupResult {
  gstin: string;
  pan: string;
  state_code: string;
  state: string;
  country: string;
  source: "local" | "external";
  note?: string;
}

export const clientsApi = {
  list: (params?: { search?: string; page?: number; page_size?: number }) =>
    api.get("/clients", { params }),

  get: (clientId: string) =>
    api.get(`/clients/${clientId}`),

  create: (payload: ClientCreatePayload) =>
    api.post("/clients", payload),

  // Stateless GSTIN parser — derives state + PAN + country from a GSTIN so
  // the quick-add modals can pre-fill those fields. Available at both
  // /clients/gstin-lookup/{gstin} and /vendors/gstin-lookup/{gstin}; we
  // call the clients surface from both modals since the response is identical.
  lookupGstin: (gstin: string) =>
    api.get<GstinLookupResult>(`/clients/gstin-lookup/${encodeURIComponent(gstin)}`),

  update: (clientId: string, payload: ClientUpdatePayload) =>
    api.patch(`/clients/${clientId}`, payload),

  // Compose-and-send an email to the client from the detail view. Sent from
  // Teamlio's default address (noreply) via the backend SendGrid helper.
  sendEmail: (clientId: string, payload: { subject: string; body: string }) =>
    api.post(`/clients/${clientId}/send-email`, payload),

  delete: (clientId: string) =>
    api.delete(`/clients/${clientId}`),

  settings: () =>
    api.get('/get_startup_khata'),

  importClients: (payload: any[]) =>
    api.post('/clients/import_clients', payload),

  importExcel: (formData: FormData) =>
    api.post("/clients/import_client_excel", formData),

  downloadImportTemplate: () =>
    downloadImportTemplate("clients", "client_import_template.xlsx"),
};

export const downloadImportTemplate = async (
  surface: "clients" | "vendors",
  filename: string,
) => {
  const response = await api.get(`/${surface}/import_template`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
